import type { Participant } from 'livekit-client';
import { Track } from 'livekit-client';
import type { TrackReferenceOrPlaceholder } from '@livekit/components-react';
import { tileKey } from './track-references.js';

/**
 * Join time in ms, or `undefined` when unknown. `Participant.joinedAt` is the
 * server's join time in whole seconds, the same on every client.
 */
function joinedAtOf(participant: Participant): number | undefined {
  const time = participant.joinedAt?.getTime();
  return time === undefined || Number.isNaN(time) ? undefined : time;
}

/** Ascending, with unknown values last and ties left to the next key. */
function compareOptional(a: number | undefined, b: number | undefined): number {
  if (a === b) return 0;
  if (a === undefined) return 1;
  if (b === undefined) return -1;
  return a - b;
}

/**
 * Orders tiles for the default grid (issue #104).
 *
 * 1. The local participant first, always.
 * 2. Everyone else by server join time, so a participant keeps their slot for
 *    the life of the call and every client sees the same order.
 * 3. Ties (join time is whole seconds) keep the order the room reports them
 *    in, which is its insertion order and does not change for people who are
 *    already present.
 * 4. Within one participant, the camera tile before any screen share.
 *
 * Nothing here looks at who is speaking or whose camera is on: either would
 * reshuffle the grid mid-call. Speaker-based ordering belongs to a future
 * spotlight mode. A side effect worth knowing: the `+N` overflow chip always
 * hides the most recent joiners, whoever is talking.
 *
 * Also collapses duplicate references, which would otherwise collide on the
 * React key.
 */
export function orderTiles(
  tracks: readonly TrackReferenceOrPlaceholder[]
): TrackReferenceOrPlaceholder[] {
  const seen = new Set<string>();
  const unique: TrackReferenceOrPlaceholder[] = [];
  const reportedOrder = new Map<string, number>();

  tracks.forEach((track) => {
    const key = tileKey(track);
    if (seen.has(key)) return;
    seen.add(key);
    unique.push(track);

    const { identity } = track.participant;
    if (!reportedOrder.has(identity)) reportedOrder.set(identity, reportedOrder.size);
  });

  const sourceRank = (track: TrackReferenceOrPlaceholder): number =>
    track.source === Track.Source.ScreenShare ? 1 : 0;

  return unique.sort((a, b) => {
    const pa = a.participant;
    const pb = b.participant;
    if (pa.isLocal !== pb.isLocal) return pa.isLocal ? -1 : 1;

    if (pa.identity !== pb.identity) {
      return (
        compareOptional(joinedAtOf(pa), joinedAtOf(pb)) ||
        (reportedOrder.get(pa.identity) ?? 0) - (reportedOrder.get(pb.identity) ?? 0)
      );
    }

    return sourceRank(a) - sourceRank(b);
  });
}
